import AddIcon from '@mui/icons-material/Add';
import ArchiveRounded from '@mui/icons-material/ArchiveRounded';
import BusinessRounded from '@mui/icons-material/BusinessRounded';
import CameraAltRounded from '@mui/icons-material/CameraAltRounded';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import DashboardRounded from '@mui/icons-material/DashboardRounded';
import EditNotifications from '@mui/icons-material/EditNotifications';
import HourglassTopRounded from '@mui/icons-material/HourglassTopRounded';
import InboxRounded from '@mui/icons-material/InboxRounded';
import MailRounded from '@mui/icons-material/MailRounded';
import ModeOfTravel from '@mui/icons-material/ModeOfTravel';
import PendingActionsRounded from '@mui/icons-material/PendingActionsRounded';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import PublishedWithChanges from '@mui/icons-material/PublishedWithChanges';
import RocketLaunch from '@mui/icons-material/RocketLaunch';
import SearchRounded from '@mui/icons-material/SearchRounded';
import WarehouseRounded from '@mui/icons-material/WarehouseRounded';
import { useMemo } from 'react';

import { useGetMailroomPiecesCountQuery, useGetPieceMenuCountersQuery } from '@api/piece';
import { Label } from '@components/Label';
import { useUserPermissions } from '@hooks/useUserPermissions';
import { Roles } from '@rdTypes/shared';
import { PATH_KT_ADMIN, administrationRoutes, inboxRoutes, mailroomRoutes } from '@routes';

export const useNavData = () => {
  const { isAllowed } = useUserPermissions();

  const { data: piecesCounters } = useGetPieceMenuCountersQuery();
  const mailroomPiecesCount = useGetMailroomPiecesCountQuery();

  const navConfig = useMemo(() => {
    const config = [];

    if (isAllowed([Roles.KTAdmin])) {
      config.push({
        subheader: 'super admin',
        items: [
          {
            id: 'onboarding',
            title: 'Onboarding',
            path: PATH_KT_ADMIN.onboarding,
            icon: <RocketLaunch />,
          },
          {
            id: 'createCompany',
            title: 'Create Company',
            path: PATH_KT_ADMIN.createCompany,
            icon: <AddIcon />,
          },
        ],
      });
    }

    if (isAllowed([Roles.Admin])) {
      config.push({
        subheader: 'administration',
        items: [
          {
            id: 'company',
            title: 'Company',
            path: 'administration/company?tab=info',
            icon: <BusinessRounded />,
          },
          {
            id: 'mailroom',
            title: 'Mailroom',
            path: 'administration/mailroom?tab=info',
            icon: <MailRounded />,
          },
          {
            id: 'users',
            title: 'Users',
            path: administrationRoutes.users,
            icon: <PeopleAltRounded />,
          },
          {
            id: 'automations',
            title: 'Automations',
            path: administrationRoutes.automations,
            icon: <PublishedWithChanges />,
          },
          {
            id: 'delivery',
            path: administrationRoutes.delivery,
            title: 'Delivery',
            icon: <ModeOfTravel />,
            children: [
              { title: 'Locations', path: administrationRoutes.locations },
              { title: 'Routes', path: administrationRoutes.routes },
            ],
          },
          {
            id: 'notificationTemlate',
            path: administrationRoutes.notificationTemplates,
            title: 'Notification Templates',
            icon: <EditNotifications />,
          },
        ],
      });
    }

    if (isAllowed([Roles.Mailroom])) {
      config.push({
        subheader: 'mailroom',
        items: [
          {
            id: 'dashboard',
            title: 'Dashboard',
            path: mailroomRoutes.dashboard,
            icon: <DashboardRounded />,
          },
          {
            id: 'receive',
            title: 'Receive',
            path: mailroomRoutes.receive,
            icon: <CameraAltRounded />,
          },
          {
            id: 'active',
            path: mailroomRoutes.root,
            title: 'Active',
            icon: <WarehouseRounded />,
            children: [
              { title: 'Items', path: mailroomRoutes.items },
              { title: 'Storage Groups', path: mailroomRoutes.storageGroups },
            ],
          },
          {
            id: 'pending-requests',
            title: 'Pending requests',
            path: mailroomRoutes.pendingRequest,
            icon: <PendingActionsRounded />,
            info: (
              <>
                {mailroomPiecesCount ? (
                  <Label variant="outlined" color="info">
                    {mailroomPiecesCount}
                  </Label>
                ) : null}
              </>
            ),
          },
          {
            id: 'search',
            title: 'Search',
            path: mailroomRoutes.search,
            icon: <SearchRounded />,
          },
        ],
      });
    }

    if (isAllowed([Roles.Recipient])) {
      config.push({
        subheader: 'Inbox',
        items: [
          {
            id: 'new',
            title: 'New',
            path: inboxRoutes.inbox('new'),
            icon: <InboxRounded />,
            info: (
              <>
                {piecesCounters?.inboxCounters.inboxNewCount ? (
                  <Label variant="outlined" color="info">
                    {piecesCounters.inboxCounters.inboxNewCount}
                  </Label>
                ) : null}
              </>
            ),
          },
          {
            id: 'processing',
            title: 'In Process',
            path: inboxRoutes.inbox('processing'),
            icon: <HourglassTopRounded />,
            info: (
              <>
                {piecesCounters?.inboxCounters.inboxInProcessCount ? (
                  <Label variant="outlined" color="info">
                    {piecesCounters.inboxCounters.inboxInProcessCount}
                  </Label>
                ) : null}
              </>
            ),
          },
          {
            id: 'processed',
            title: 'Completed',
            path: inboxRoutes.inbox('processed'),
            icon: <CheckCircleOutlineRounded />,
            info: (
              <>
                {piecesCounters?.inboxCounters.inboxCompletedCount ? (
                  <Label variant="outlined" color="info">
                    {piecesCounters.inboxCounters.inboxCompletedCount}
                  </Label>
                ) : null}
              </>
            ),
          },
          {
            id: 'archive',
            title: 'Archive',
            path: inboxRoutes.inbox('archive'),
            icon: <ArchiveRounded />,
          },
        ],
      });
    }

    return config;
  }, [isAllowed, mailroomPiecesCount, piecesCounters]);

  return navConfig;
};
